import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "src/redux/hooks";
import useLayout from "src/hooks/useLayout.hook";

import BottomBar from "./BottomBar";

import { setThemeColors } from "src/helpers/common";
import supportPrograms from "src/data/app/programs";
import { generateTheme } from "src/helpers/theme";
import useTheme from "src/hooks/useTheme";

interface LayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  stickyHeader?: boolean;
  footer?: React.ReactNode;
  stickyFooter?: boolean;
  showBottomBar?: boolean;
  supportProgramKey: string;
  enableTheme?: boolean;
  className?: string;
  containerClassName?: string;
  bodyClassName?: string;
  backgroundLinearGradient?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  header,
  stickyHeader = false,
  footer,
  stickyFooter = false,
  showBottomBar = false,
  supportProgramKey,
  enableTheme = false,
  className = "",
  containerClassName = "",
  bodyClassName = "",
  backgroundLinearGradient = false,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);
  const showBottomBarState = useAppSelector(
    (state) => state.config.appLayout.showBottomBar,
  );
  useLayout();
  const theme = useTheme(supportProgramKey);
  const params = useParams();
  const supportProgramKeyFromParams = params["support-program-key"];
  const supportProgramKeyNew = supportProgramKey || supportProgramKeyFromParams;
  const config =
    supportPrograms[supportProgramKeyNew as keyof typeof supportPrograms];

  const hasCustomFooter = !!footer;
  const useBottomBar = !footer && showBottomBarState && showBottomBar;
  const showFooterArea =
    hasCustomFooter || (useBottomBar && showBottomBarState && showBottomBar);

  const appScreens = config?.app?.screens;
  const bottomBarItems =
    useBottomBar && appScreens && typeof appScreens === "object"
      ? Object.keys(appScreens).map((key) => {
          const screen = (appScreens as Record<string, any>)[key];
          return {
            screenName: screen?.title,
            icon: screen?.icon,
            activeIcon: screen?.activeIcon,
            screenKey: screen?.id,
          };
        })
      : [];

  useEffect(() => {
    if (enableTheme) {
      // setThemeColors(config?.theme?.colors);
      setThemeColors(generateTheme(config?.theme?.colors));
    }
  }, [enableTheme]);

  // Measure header on mount and when header/footer refs are available
  useEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight ?? 0);
    setFooterHeight(
      showFooterArea ? (footerRef?.current?.clientHeight ?? 0) : 0,
    );
  }, [showFooterArea]);

  // Re-measure header whenever its size changes (e.g. custom header content like tabs/vitals)
  useEffect(() => {
    const headerEl = headerRef?.current;
    if (!headerEl || !header) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height =
          entry.contentRect?.height ?? headerRef?.current?.clientHeight ?? 0;
        setHeaderHeight(height);
      }
    });

    resizeObserver.observe(headerEl);
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observe once when header node exists; ResizeObserver handles dynamic height
  }, []);

  const footerContent = hasCustomFooter ? (
    footer
  ) : useBottomBar && showBottomBarState && showBottomBar ? (
    <BottomBar
      bottomBarItems={bottomBarItems}
      supportProgramKey={supportProgramKeyNew || ""}
    />
  ) : null;

  return (
    <div className={`h-screen ${containerClassName}`}>
      <div
        className={`max-w-lg mx-auto flex flex-col h-screen w-full ${className}`}
        style={{
          paddingTop: `${headerHeight}px`,
          paddingBottom: `${footerHeight}px`,
          overflow: "hidden",
          background: theme.colors?.primaryBackground,
          // background: backgroundLinearGradient
          //   ? `linear-gradient(135deg,
          //   ${theme?.colors?.primaryBackground} 0%,
          //     ${theme?.colors?.primaryBackground} 20%,
          //     ${theme?.colors?.secondaryBackground} 60%,
          //     ${theme?.colors?.secondaryBackground} 100%)`
          //   : "transparent",
        }}
      >
        {header && (
          <div
            className="fixed top-0 z-10 max-w-lg mx-auto w-full"
            ref={headerRef}
          >
            {header}
          </div>
        )}
        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain ${bodyClassName}`}
        >
          {children}
        </div>
        {footerContent && (
          <div
            className="fixed bottom-0 z-0 max-w-lg mx-auto w-full"
            ref={footerRef}
          >
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;
